import React, { useMemo } from 'react';
import AutoSizeTitle from '../../components/autoSizeTitle/AutoSizeTitle';
import IndividualInformHeader from '../../components/individualInformHeader/individualInformHeader';
import PredictiveMatrix2x2Selfcare, {
    getMatrixZone,
} from '../../graphics/selfcare/predictiveMatrix2x2Selfcare';
import {
    getDimensionByCode,
    getMatrixAction,
    getMatrixInterpretation,
    getMatrixMessage,
    SELFCARE_MATRIX_CRITICAL_THRESHOLD,
    SELFCARE_MATRIX_PROTECTIVE_THRESHOLD,
} from './selfcarePredictiveMatrices';
import { roundHalfUp } from './selfcareReportLogic';
import './pagePredictiveMatrixSelfcare.css';

export const PredictiveMatrixBlockSelfcare = ({
    individualResult,
    matrixDef,
    xScore: externalXScore,
    yScore: externalYScore,
    compact = false,
}) => {
    const xScore = useMemo(() => {
        if (externalXScore !== undefined) return externalXScore;
        return getDimensionByCode(individualResult, matrixDef?.xDimension)?.average ?? null;
    }, [externalXScore, individualResult, matrixDef]);

    const yScore = useMemo(() => {
        if (externalYScore !== undefined) return externalYScore;
        return getDimensionByCode(individualResult, matrixDef?.yDimension)?.average ?? null;
    }, [externalYScore, individualResult, matrixDef]);

    const zone = useMemo(() => getMatrixZone(xScore, yScore), [xScore, yScore]);
    const interpretation = useMemo(() => getMatrixInterpretation(matrixDef, zone?.key), [matrixDef, zone]);
    const action = useMemo(() => getMatrixAction(zone?.key), [zone]);
    const message = useMemo(() => getMatrixMessage(matrixDef, zone?.key), [matrixDef, zone]);

    const boxClass = compact ? 'selfcare-matrix-box selfcare-matrix-box--compact' : 'selfcare-matrix-box';
    const gridClass = compact ? 'selfcare-matrix-grid selfcare-matrix-grid--compact' : 'selfcare-matrix-grid';
    const rightClass = compact ? 'selfcare-matrix-right selfcare-matrix-right--compact' : 'selfcare-matrix-right';
    const matrixSize = compact ? 170 : 190;

    return (
        <div className={boxClass}>
            <div className={gridClass}>
                <div>
                    <div
                        className={compact ? 'selfcare-matrix-block-title selfcare-matrix-block-title--compact' : 'selfcare-matrix-block-title'}
                    >
                        {matrixDef?.matrixTitle}
                    </div>
                    <PredictiveMatrix2x2Selfcare
                        xLabel={matrixDef?.xLabel}
                        yLabel={matrixDef?.yLabel}
                        xValue={xScore}
                        yValue={yScore}
                        size={matrixSize}
                    />
                </div>

                <div className={rightClass}>
                    <div>
                        <div className={compact ? 'selfcare-matrix-subtitle selfcare-matrix-subtitle--compact' : 'selfcare-matrix-subtitle'}>
                            Propósito del cruce
                        </div>
                        <div className={compact ? 'selfcare-matrix-body selfcare-matrix-body--compact' : 'selfcare-matrix-body'}>
                            {matrixDef?.purpose}
                        </div>
                    </div>

                    <div>
                        <div className={compact ? 'selfcare-matrix-subtitle selfcare-matrix-subtitle--compact' : 'selfcare-matrix-subtitle'}>
                            Resultados del operario
                        </div>
                        <ul className={compact ? 'selfcare-matrix-bullets selfcare-matrix-bullets--compact' : 'selfcare-matrix-bullets'}>
                            <li>
                                {matrixDef?.xLabel} (X): {xScore == null ? '—' : `${roundHalfUp(xScore, 2)}/100`}
                            </li>
                            <li>
                                {matrixDef?.yLabel} (Y): {yScore == null ? '—' : `${roundHalfUp(yScore, 2)}/100`}
                            </li>
                            <li>
                                Ubicación en la matriz:{' '}
                                <span style={{ fontWeight: 800 }}>{zone?.label}</span>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <div className={compact ? 'selfcare-matrix-subtitle selfcare-matrix-subtitle--compact' : 'selfcare-matrix-subtitle'}>
                            Interpretación
                        </div>
                        <div className={compact ? 'selfcare-matrix-body selfcare-matrix-body--compact' : 'selfcare-matrix-body'}>
                            {interpretation}
                        </div>
                    </div>

                    <div>
                        <div className={compact ? 'selfcare-matrix-subtitle selfcare-matrix-subtitle--compact' : 'selfcare-matrix-subtitle'}>
                            Acción clave
                        </div>
                        <div className={compact ? 'selfcare-matrix-body selfcare-matrix-body--compact' : 'selfcare-matrix-body'}>
                            {action}
                        </div>
                    </div>

                    <div>
                        <div className={compact ? 'selfcare-matrix-subtitle selfcare-matrix-subtitle--compact' : 'selfcare-matrix-subtitle'}>
                            Mensaje
                        </div>
                        <div className={compact ? 'selfcare-matrix-body selfcare-matrix-body--compact' : 'selfcare-matrix-body'}>
                            {message}
                        </div>
                    </div>
                </div>
            </div>

            {matrixDef?.whatCanHappen && (
                <div className={compact ? 'selfcare-matrix-footer selfcare-matrix-footer--compact' : 'selfcare-matrix-footer'}>
                    <b>Qué puede pasar:</b> {matrixDef.whatCanHappen}
                </div>
            )}
        </div>
    );
};

const PagePredictiveMatrixSelfcare = ({
    selectedEvaluation,
    individualResult,
    orgLogo,
    pageNumber,
    matrixDef,
    xScore,
    yScore,
    showIntro = false,
}) => {
    const backgroundStyle = {
        backgroundImage: `url('/informs/pagina.png')`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'left top',
        backgroundSize: '100% auto',
    };

    return (
        <div className="pdf-page internal-page">
            <div className="internal-page-content" style={backgroundStyle}>
                <AutoSizeTitle text={selectedEvaluation?.evl_title} />

                <div className="internal-page-info internal-page-info-individual">
                    <IndividualInformHeader individualResult={individualResult} orgLogo={orgLogo} />

                    <div className="selfcare-matrix-title">Análisis Predictivo Matriz 2x2</div>

                    {showIntro && (
                        <>
                            <div className="selfcare-matrix-intro">
                                En seguridad, el riesgo rara vez se explica por un solo factor. Muchas condiciones se
                                combinan (por ejemplo: cansancio + presión emocional). Por eso, además de puntajes por
                                dimensión, este informe incluye matrices 2x2 para identificar combinaciones que aumentan
                                o reducen la probabilidad de error humano.
                                <br />
                                <br />
                                <b>Importante:</b> Esta matriz no evalúa a la persona. Evalúa la condición actual en la que
                                estás hoy. Esa condición puede cambiar y mejorar con acciones concretas.
                            </div>

                            <div className="selfcare-matrix-legend">
                                <div className="selfcare-matrix-legend-col">
                                    <div className="selfcare-matrix-tier">
                                        <span className="selfcare-matrix-dot" style={{ backgroundColor: '#43A047' }} />
                                        <span className="selfcare-matrix-range">≥{SELFCARE_MATRIX_PROTECTIVE_THRESHOLD} y ≥{SELFCARE_MATRIX_PROTECTIVE_THRESHOLD}</span>
                                        <span className="selfcare-matrix-tier-name">ZONA PROTECTORA</span>
                                    </div>
                                    <div className="selfcare-matrix-tier">
                                        <span className="selfcare-matrix-dot" style={{ backgroundColor: '#FDD835' }} />
                                        <span className="selfcare-matrix-range">Uno ≥{SELFCARE_MATRIX_PROTECTIVE_THRESHOLD}, otro &lt;{SELFCARE_MATRIX_PROTECTIVE_THRESHOLD}</span>
                                        <span className="selfcare-matrix-tier-name">ZONA MIXTA</span>
                                    </div>
                                    <div className="selfcare-matrix-tier">
                                        <span className="selfcare-matrix-dot" style={{ backgroundColor: '#FB8C00' }} />
                                        <span className="selfcare-matrix-range">Ambos &lt;{SELFCARE_MATRIX_PROTECTIVE_THRESHOLD}, al menos uno ≥{SELFCARE_MATRIX_CRITICAL_THRESHOLD}</span>
                                        <span className="selfcare-matrix-tier-name">ZONA MIXTA ELEVADA</span>
                                    </div>
                                    <div className="selfcare-matrix-tier">
                                        <span className="selfcare-matrix-dot" style={{ backgroundColor: '#E53935' }} />
                                        <span className="selfcare-matrix-range">&lt;{SELFCARE_MATRIX_CRITICAL_THRESHOLD} y &lt;{SELFCARE_MATRIX_CRITICAL_THRESHOLD}</span>
                                        <span className="selfcare-matrix-tier-name">ZONA CRÍTICA</span>
                                    </div>
                                </div>
                                <div className="selfcare-matrix-legend-text">
                                    <b>Lectura por cuadrantes</b> (interpretación general)
                                    <br />
                                    Protectora (≥{SELFCARE_MATRIX_PROTECTIVE_THRESHOLD} y ≥{SELFCARE_MATRIX_PROTECTIVE_THRESHOLD}): ambas dimensiones en rango seguro.
                                    <br />
                                    Mixta (un valor ≥{SELFCARE_MATRIX_PROTECTIVE_THRESHOLD} y otro &lt;{SELFCARE_MATRIX_PROTECTIVE_THRESHOLD}): vulnerabilidad parcial.
                                    <br />
                                    Mixta elevada (ambos &lt;{SELFCARE_MATRIX_PROTECTIVE_THRESHOLD}, al menos uno ≥{SELFCARE_MATRIX_CRITICAL_THRESHOLD}): riesgo combinado moderado-alto.
                                    <br />
                                    Crítica (&lt;{SELFCARE_MATRIX_CRITICAL_THRESHOLD} y &lt;{SELFCARE_MATRIX_CRITICAL_THRESHOLD}): ambas dimensiones en rango crítico.
                                </div>
                            </div>
                        </>
                    )}

                    <PredictiveMatrixBlockSelfcare
                        individualResult={individualResult}
                        matrixDef={matrixDef}
                        xScore={xScore}
                        yScore={yScore}
                    />
                </div>

                <div className="internal-page-number">{pageNumber}</div>
            </div>
        </div>
    );
};

export default PagePredictiveMatrixSelfcare;
